import styled from '@emotion/styled';
import { useState, useCallback } from 'react';

import {
  useManualRetaker,
  ManualRetakerUrlModal,
  MANUAL_RETAKER,
  CONTENT_RIGHT_LEFT_PADDING,
  RefreshScreenshotModal,
} from '@hotjar/heatmaps';
import { tokens } from '@hotjar/hotsauce';
import { useShortLinkWidgetHiddenState } from '@hotjar/webapp/features/ShortLinkWidget';
import { RecordingsRetakerModal } from '@hotjar/webapp/features/Heatmaps/components/RecordingsRetaker/RecordingsRetakerModal';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import type { Site } from '@hotjar/site';
import {
  EVENT_TRIGGERS,
  EVENT_UI_POSITIONS,
  EVENT_TRIGGERED_ON,
} from '@hotjar/webapp/domains/heatmaps/tracking';
import { useFeatureFlag } from '@hotjar/webapp/hooks/useFeatureFlag';

import type { ReportProps } from '../../ContinuousCaptureReport/Report/ReportTypes';
import { CONTINUOUS_TYPES } from './constants';
import { StatusBarActions as StatusBarActionsLegacy } from './StatusBarActionsLegacy';
import { StatusBarCore as StatusBarCoreLegacy } from './StatusBarCoreLegacy';

const HeatmapStatsContainer = styled.div`
  justify-content: space-between;
  align-items: center;
  display: flex;
  padding: ${tokens.spacer.x0pt5} ${tokens.spacer.x5};
  width: 100%;
`;

const StyledStatusBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: calc(100% + ${CONTENT_RIGHT_LEFT_PADDING} + ${CONTENT_RIGHT_LEFT_PADDING});
  // Negative margin to offset/counter the padding of the main parent container
  margin-left: -${CONTENT_RIGHT_LEFT_PADDING};
  border-color: ${tokens.color.ui_border};
  border-width: 1px 0px 1px 0px;
  border-style: solid;
  ${tokens.text('body2')};
`;

function setDocumentOverflowY(value: 'hidden' | 'auto'): void {
  document.body.style.overflowY = value;
}

/**
 * Render a heatmap status bar with page view count and screenshot information.
 * This component shows a popover when the screenshot info is hovered and allows
 * the user to start the retake process.
 *
 * FIXME: fix the props description
 * @param lastScreenshotTimestamp: the last time the heatmap screenshot was taken (the recording date used in generating the heatmap) in epoch
 * @param onRetakeScreenshotClicked: a callback function to start the retake screenshot flow
 * @param highlights: properties related to highlights feature
 * @param filters: JSON object with current filters
 * @param count: The count of clicks or taps of the heatmap
 * @param dataType: The heatmap data type
 */
interface StatusBarProps {
  highlights: {
    activate: () => void;
    deactivate: () => void;
    isActive: boolean;
  };
  onDownloadImage: () => void;
  onDownloadData: () => void;
  selectedDeviceType: HeatmapDeviceTypes;
  isSinglePageSearch: boolean;
  captureType: CONTINUOUS_TYPES;
  onRetakeScreenshotClicked: ReportProps['onRetakeScreenshotClicked'];
  lastScreenshotTimestamp: number;
  isSelectedDateRangeAbsolute: boolean;
  screenshotData?: HeatmapAPI.GetScreenshotDataResponse;
  currentSite: Site | undefined;
  filtersApplied: boolean;
}

export const StatusBar = ({
  captureType,
  lastScreenshotTimestamp,
  isSelectedDateRangeAbsolute,
  onRetakeScreenshotClicked,
  highlights,
  onDownloadImage,
  onDownloadData,
  isSinglePageSearch,
  selectedDeviceType,
  screenshotData,
  currentSite,
  filtersApplied,
}: StatusBarProps): JSX.Element => {
  const shouldUseManualRetaker = useFeatureFlag(MANUAL_RETAKER);

  const [isRefreshScreenshotModalOpen, setRefreshScreenshotModalOpen] = useState(false);
  const [isRecordingsRetakerModalOpen, setRecordingsRetakerModalOpen] = useState(false);
  const { setIsHidden: setIsShortLinkWidgetHidden } = useShortLinkWidgetHiddenState();
  const track = useTrack();

  const onManualRetakerSuccess = useCallback(
    (html: string, sourceTrigger: RetakerTriggerSource) => {
      onRetakeScreenshotClicked({ html }, true, sourceTrigger);
    },
    [onRetakeScreenshotClicked],
  );

  const {
    handleManualRetakerClick,
    handleManualRetakerUrlModalClose,
    handleManualRetakerUrlSubmit,
    isManualRetakerUrlModalOpen,
  } = useManualRetaker(selectedDeviceType, onManualRetakerSuccess);

  const trackRetakeStarted = (trigger: string) => {
    if (currentSite) {
      track.click(trigger, EVENT_UI_POSITIONS.REPORT, track.getGroupDataForSite(currentSite), {
        state_event_was_triggered_on: EVENT_TRIGGERED_ON.REPORT,
      });
    }
  };

  const handleScreenshotRetake = () => {
    if (captureType === CONTINUOUS_TYPES.SNAPSHOT_UNLIMITED) {
      onRetakeScreenshotClicked();
    } else {
      setRefreshScreenshotModalOpen(true);
      trackRetakeStarted(EVENT_TRIGGERS.REFRESH);
    }
  };

  const handleScreenshotTakeNew = () => {
    if (shouldUseManualRetaker) {
      handleManualRetakerClick((isSinglePageSearch && screenshotData?.originUrl) || '');
      trackRetakeStarted(EVENT_TRIGGERS.MANUAL_RETAKE);
    } else {
      setRecordingsRetakerModalOpen(true);
      setIsShortLinkWidgetHidden(true);
      setDocumentOverflowY('hidden');
      trackRetakeStarted(EVENT_TRIGGERS.TAKE_NEW);
    }
  };

  const handleRetakeScreenshotConfirmation = () => {
    onRetakeScreenshotClicked();
    setRefreshScreenshotModalOpen(false);
  };

  const handelRecordingsRetakerModalClose = () => {
    setRecordingsRetakerModalOpen(false);
    setIsShortLinkWidgetHidden(false);
    setDocumentOverflowY('auto');
  };

  const handleRecordingsRetakerRetakeScreenshot = (selectedTimeInMS: number) => {
    setRecordingsRetakerModalOpen(false);
    setIsShortLinkWidgetHidden(false);
    setDocumentOverflowY('auto');
    // Call API with new start Time
    onRetakeScreenshotClicked({
      startTime: selectedTimeInMS,
      recordingId: screenshotData?.recordingId as number,
      previousStartTime: screenshotData?.recordingStartTime as number,
    });
  };

  return (
    <>
      <StyledStatusBar>
        <HeatmapStatsContainer>
          <StatusBarCoreLegacy
            screenshotDate={lastScreenshotTimestamp}
            onRetake={handleScreenshotRetake}
            onTakeNew={handleScreenshotTakeNew}
          />

          <StatusBarActionsLegacy
            filtersApplied={filtersApplied}
            highlights={highlights}
            site={currentSite}
            onDownloadData={onDownloadData}
            onDownloadImage={onDownloadImage}
          />
        </HeatmapStatsContainer>
      </StyledStatusBar>
      <RefreshScreenshotModal
        isSelectedDateRangeAbsolute={isSelectedDateRangeAbsolute}
        isOpen={isRefreshScreenshotModalOpen}
        onCancelOrClose={() => setRefreshScreenshotModalOpen(false)}
        onRefreshScreenshot={handleRetakeScreenshotConfirmation}
      />
      <RecordingsRetakerModal
        isOpen={isRecordingsRetakerModalOpen}
        onCancelOrClose={handelRecordingsRetakerModalClose}
        onRetakeScreenshot={handleRecordingsRetakerRetakeScreenshot}
        recordingId={screenshotData?.recordingId}
        recordingStartTime={screenshotData?.recordingStartTime}
      />
      <ManualRetakerUrlModal
        isOpen={isManualRetakerUrlModalOpen}
        onSubmit={handleManualRetakerUrlSubmit}
        onClose={handleManualRetakerUrlModalClose}
      />
    </>
  );
};
